import { useEffect, useState } from 'react';

import { bindAll } from 'bind-event-listener';
import omit from 'lodash/omit';
import { useDebouncedCallback } from 'use-debounce';

import type { SuggestionConfig, CrossAppCreationConfig } from '../types';

export const RESIZING_DEBOUNCED = 100;

const MOBILE_WIDTH_BREAKPOINT = 767;

export function useIsResponsiveView(): boolean {
	const [isResponsiveMode, setIsResponsiveMode] = useState(
		window.innerWidth <= MOBILE_WIDTH_BREAKPOINT,
	);

	const [handleResizeDebounced] = useDebouncedCallback(() => {
		const isCurrentlyMobile = window.innerWidth <= MOBILE_WIDTH_BREAKPOINT;
		setIsResponsiveMode((prev) => (prev !== isCurrentlyMobile ? isCurrentlyMobile : prev));
	}, RESIZING_DEBOUNCED);

	useEffect(() => {
		let unbind;
		unbind = bindAll(window, [
			{
				type: 'resize',
				listener: handleResizeDebounced,
			},
		]);

		return unbind;
	}, [handleResizeDebounced]);

	return isResponsiveMode;
}

// A safer omit, omitted properties must exist on the item type
export const omitSafely = <T extends object, P extends (keyof T)[]>(
	item: T,
	...properties: P
): Omit<T, P[number]> => omit(item, ...properties);

/**
 * Get the cloud ID from either V1 (suggestionConfig) or V2 (crossAppCreationConfig) configuration.
 */
export const getCloudId = (contextConfig: SuggestionConfig | CrossAppCreationConfig) =>
	contextConfig.enabled ? contextConfig.cloudId : '';

/**
 * Get the org ID from either V1 (suggestionConfig) or V2 (crossAppCreationConfig) configuration.
 */
export const getOrgId = (contextConfig: SuggestionConfig | CrossAppCreationConfig) =>
	contextConfig.enabled ? contextConfig.orgId : undefined;
